/**
 * 配信 CMS の portal 面エンドポイント。
 *
 * **パス文字列をここ以外に書かない。** 画面が直接 `api.get('/content/...')` を
 * 書くと、API 側のパス変更が全画面に散る。`lib/api.ts` の薄いラッパを
 * さらに 1 枚かぶせて、パスとクエリの組み立てをこのファイルに閉じ込める。
 *
 * 出典:
 * - `docs/CMS_DESIGN.md` §5.3（portal 面のパス一覧）
 * - `docs/CMS_GAP_AND_API.md` §5.8（Webhook の管理 API）
 *
 * 本文（ブロック JSON / HTML）が通るのは
 * `getRevision` / `saveRevision` / `previewContent` の 3 本だけで、
 * これは CD §3.3 が許した「保存 / ビルド / プレビュー」に対応する。
 */
import { api, downloadFile, newIdempotencyKey, requestText } from './api.js';
import { CAMPAIGN_STATUS, campaignStatusFromDb, campaignStatusToDb } from '@mail-sv/shared';
import type { BlocksDocument, CampaignStatus } from '@mail-sv/shared';
import type { CountedList, CursorPage } from './api-types.js';
import type {
  CampaignCounts,
  CampaignDetail,
  CampaignClickReport,
  CampaignReport,
  CampaignReportCsvKind,
  CampaignStateChange,
  CampaignSummary,
  CampaignTestSendResult,
  CampaignValidation,
  Contact,
  ContactImport,
  ContactImportCreated2,
  ContactList,
  ContentArtifact,
  ContentArtifactBuilding,
  ContentAsset,
  ContentAssetFolder,
  ContentAssetReplaceTicket,
  ContentAssetUploadTicket,
  ContentAssetUsage,
  ContentDocumentPage,
  ContentDocumentSummary,
  ContentExportAccepted,
  ContentExportStatus,
  ContentImport,
  ContentImportApplied,
  ContentImportCreated,
  ContentImportValidation,
  ContentLinkCheckPage,
  ContentLinkCheckRun,
  ContentLinkPolicy,
  ContentLock,
  ContentPreviewResult,
  ContentRevisionBody,
  ContentRevisionDiff,
  ContentRevisionMeta,
  ContentRevisionSaved,
  ContentSchedule,
  ContentShareLink,
  ContentShareLinkCreated,
  ContentTag,
  ContentTagDeleted,
  ContentTagMerged,
  ContentTemplate,
  OrgContentPolicy,
  PendingReview,
  SnippetDependents,
  Suppression,
  UnusedAssetPage,
  WebhookDelivery,
  WebhookEndpoint,
  WebhookEndpointCreated,
  WebhookReplayPending,
  WebhookReplayResult,
  WebhookSecretRotated,
  WebhookTestResult,
} from './content-types.js';

/** クエリ文字列。空文字・undefined・'all' は「絞り込まない」として落とす。 */
export function toQuery(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    const s = String(value);
    if (s === '' || s === 'all') continue;
    sp.set(key, s);
  }
  const q = sp.toString();
  return q ? `?${q}` : '';
}

const enc = encodeURIComponent;

/* ============================================================ コンテンツ文書 */

export interface ContentListFilter {
  q?: string;
  kind?: string;
  tag?: string;
  reviewState?: string;
  updatedBy?: string;
  updatedFrom?: string;
  updatedTo?: string;
  archived?: boolean;
  origin?: string;
  sort?: string;
  cursor?: string | null;
  limit?: number;
}

export const contentApi = {
  listDocuments: (filter: ContentListFilter): Promise<ContentDocumentPage> =>
    api.get<ContentDocumentPage>(
      `/content${toQuery({
        q: filter.q,
        kind: filter.kind,
        tag: filter.tag,
        reviewState: filter.reviewState,
        updatedBy: filter.updatedBy,
        updatedFrom: filter.updatedFrom,
        updatedTo: filter.updatedTo,
        // **`archived=false` を送ってはいけない。** サーバー側は `z.coerce.boolean()` で
        // 受けるため、文字列 "false" は true に化ける。既定（未指定）が「含めない」。
        archived: filter.archived === true ? 'true' : undefined,
        origin: filter.origin,
        sort: filter.sort,
        cursor: filter.cursor,
        limit: filter.limit,
      })}`,
    ),

  getDocument: (id: string): Promise<ContentDocumentSummary> =>
    api.get<ContentDocumentSummary>(`/content/${enc(id)}`),

  createDocument: (body: {
    kind: string;
    name: string;
    subject?: string;
    preheader?: string;
    tags?: string[];
  }): Promise<ContentDocumentSummary> =>
    api.post<ContentDocumentSummary>('/content', body, { idempotencyKey: newIdempotencyKey() }),

  /**
   * メタの更新。
   *
   * **`campaignId` は後から張れる**（API 側で追加された）。キャンペーンの作成には
   * 文書 ID が要るので文書が先に生まれるが、ビルド時の法定フッタは
   * `campaign_id` 経由でしか宛先リストへ辿れない。紐づけたら**必ず再ビルドする**。
   * 紐づけ忘れ / 再ビルド忘れは `has_legal_footer=false` のまま固定され、
   * プリフライトが恒久的に `LEGAL_FOOTER_MISSING` で落ちて 1 通も送れなくなる。
   */
  updateDocument: (
    id: string,
    body: {
      name?: string;
      subject?: string;
      preheader?: string;
      tags?: string[];
      campaignId?: string | null;
      externalRef?: string | null;
    },
  ): Promise<ContentDocumentSummary> =>
    api.patch<ContentDocumentSummary>(`/content/${enc(id)}`, body),

  duplicateDocument: (id: string, name: string): Promise<ContentDocumentSummary> =>
    api.post<ContentDocumentSummary>(`/content/${enc(id)}/duplicate`, { name }),

  /** 論理削除（アーカイブ）。物理削除の口は画面にも API にも出さない。 */
  archiveDocument: (id: string): Promise<void> => api.del<void>(`/content/${enc(id)}`),

  unarchiveDocument: (id: string): Promise<void> =>
    api.post<void>(`/content/${enc(id)}/unarchive`),

  listRevisions: (id: string): Promise<CursorPage<ContentRevisionMeta>> =>
    api.get<CursorPage<ContentRevisionMeta>>(`/content/${enc(id)}/revisions`),

  /** メタのみ。**`rev` は正の整数しか受け付けない**（`latest` は無い）。 */
  getRevisionMeta: (id: string, revision: number): Promise<ContentRevisionMeta> =>
    api.get<ContentRevisionMeta>(`/content/${enc(id)}/revisions/${revision}`),

  /** 本文つきで 1 版取得。API が S3 から読んでレスポンスにだけ載せる（CD §3.3-1 の逆）。 */
  getRevisionBody: (id: string, revision: number): Promise<ContentRevisionBody> =>
    api.get<ContentRevisionBody>(`/content/${enc(id)}/revisions/${revision}/body?inline=1`),

  /**
   * 保存。`baseRevision` は楽観ロックで、他人が先に保存していたら 409 で弾かれる。
   * 3-way マージは意図的に持たない（CD §7）。人間に判断させる。
   */
  saveRevision: (
    id: string,
    body: { doc: BlocksDocument; baseRevision?: number },
  ): Promise<ContentRevisionSaved> =>
    api.put<ContentRevisionSaved>(`/content/${enc(id)}/revisions`, body),

  /** ビルド成果物のメタ（テキスト版の URL・lint・サイズ）。未ビルドなら `state='building'`。 */
  getArtifact: (id: string, revision: number): Promise<ContentArtifact | ContentArtifactBuilding> =>
    api.get<ContentArtifact | ContentArtifactBuilding>(
      `/content/${enc(id)}/revisions/${revision}/artifact`,
    ),

  /**
   * プレビュー。**`text/html` をそのまま返す**エンドポイントなので JSON として読まない。
   * ビルドがまだなら 202 + JSON が返る。
   */
  previewContent: async (id: string, revision?: number): Promise<ContentPreviewResult> => {
    const res = await requestText('POST', `/content/${enc(id)}/preview`, {
      body: revision === undefined ? {} : { revision },
    });
    if (res.status === 202) return { state: 'building' };
    return { state: 'ready', html: res.body };
  },

  /**
   * 承認依頼。**リビジョン単位**で出す（どの版に責任を持つかが記録の本体なので、
   * 文書単位で依頼を出す口は用意されていない）。
   */
  submitReview: (
    id: string,
    revision: number,
    note?: string,
  ): Promise<{ reviewId: string; reviewState: string }> =>
    api.post<{ reviewId: string; reviewState: string }>(
      `/content/${enc(id)}/revisions/${revision}/review`,
      note ? { note } : {},
    ),

  /** 未決の承認依頼。編集画面は `contentRevisionId` で自分の版のものを探す。 */
  pendingReviews: (): Promise<CursorPage<PendingReview>> =>
    api.get<CursorPage<PendingReview>>('/content/reviews'),

  /**
   * 承認 / 差し戻し / 取り下げ。**依頼の ID に対して行う**。
   * URL の語尾はケバブケース（`request-changes`）で、遷移表のイベント名
   * （`requestChanges`）とは綴りが違う点に注意。
   */
  decideReview: (
    reviewId: string,
    action: 'approve' | 'request-changes' | 'withdraw',
    note?: string,
  ): Promise<{ reviewState: string }> =>
    api.post<{ reviewState: string }>(
      `/content/reviews/${enc(reviewId)}/${action}`,
      note ? { note } : {},
    ),

  /**
   * 版と版の差分（[52]）。**S3 から都度生成し、結果は保存しない**（CD §5.3）。
   * このレスポンスにだけ本文の実文（要約）が出る。プレビューと同じ区分なので、
   * 画面もローカルストレージ等に**残さないこと**。
   *
   * 戻り値は `@mail-sv/shared` の `RevisionDiff` **ではない**。API は
   * 自前のローカル実装を使っていてリンク先ドメインの増減を返さない。
   * 詳細は `ContentRevisionDiff` のコメントを見ること。
   */
  diffRevisions: (id: string, a: number, b: number): Promise<ContentRevisionDiff> =>
    api.get<ContentRevisionDiff>(`/content/${enc(id)}/revisions/${a}/diff/${b}`),

  /** ビルドの積み直し（本文を変えずに成果物だけ作り直す）。 */
  rebuildRevision: (id: string, revision: number): Promise<{ jobId: string }> =>
    api.post<{ jobId: string }>(`/content/${enc(id)}/revisions/${revision}/rebuild`),

  /* ---------------------------------------------------------- ソフトロック */

  /** 取得（TTL 5 分）。他人が保持していれば 409。 */
  acquireLock: (id: string): Promise<ContentLock> => api.post<ContentLock>(`/content/${enc(id)}/lock`),

  /**
   * 解除。`force=true` は**他人の編集を捨てる操作**なので `contentApprove` が要る
   * （API 側で 403）。画面も承認権限を持つ人にしかボタンを出さない。
   *
   * `keepalive` は「画面を閉じる直前に投げる」ときだけ真にする。
   * 付けないとブラウザが遷移でリクエストを捨てるため、
   * **ロックが TTL の 5 分間残って他の担当者が編集に入れない**。
   */
  releaseLock: (
    id: string,
    force = false,
    opts: { keepalive?: boolean } = {},
  ): Promise<{ released: boolean }> =>
    api.del<{ released: boolean }>(`/content/${enc(id)}/lock${force ? '?force=true' : ''}`, {
      keepalive: opts.keepalive ?? false,
    }),

  /* -------------------------------------------------------------- タグ */

  listTags: (): Promise<CountedList<ContentTag>> => api.get<CountedList<ContentTag>>('/content/tags'),

  createTag: (body: {
    slug: string;
    label: string;
    kind: string;
    color?: string;
    description?: string;
  }): Promise<ContentTag> => api.post<ContentTag>('/content/tags', body),

  /**
   * 改名。`slug` を変えると API が `content_documents.tags` /
   * `content_assets.tags` を**同一トランザクションで**書き換える。
   */
  updateTag: (
    tagId: string,
    body: { slug?: string; label?: string; color?: string; description?: string | null; kind?: string },
  ): Promise<ContentTag> => api.patch<ContentTag>(`/content/tags/${enc(tagId)}`, body),

  /** 使用中ならアーカイブ、未使用なら物理削除。どちらだったかは応答で分かる。 */
  deleteTag: (tagId: string): Promise<ContentTagDeleted> =>
    api.del<ContentTagDeleted>(`/content/tags/${enc(tagId)}`),

  /** 統合。`tagId` を `intoTagId` に寄せる（元のタグはアーカイブされる）。 */
  mergeTag: (tagId: string, intoTagId: string): Promise<ContentTagMerged> =>
    api.post<ContentTagMerged>(`/content/tags/${enc(tagId)}/merge`, { intoTagId }),

  /* -------------------------------------------------- テンプレート・共有 */

  listTemplates: (): Promise<CountedList<ContentTemplate>> =>
    api.get<CountedList<ContentTemplate>>('/content-templates'),

  /**
   * いま編集しているコンテンツを**自組織のテンプレート**（`scope='org'`）にする。
   *
   * 版はコピー元のリビジョンを指すだけで本文は複製しない（複製は適用の
   * 瞬間に S3 側で行う）。**この口が画面から呼ばれていなかったため、
   * 契約者は自分でテンプレートを作れず、運営が配る共有ぶんしか使えなかった。**
   */
  saveAsTemplate: (
    id: string,
    body: { name: string; category?: string; description?: string },
  ): Promise<ContentTemplate> =>
    api.post<ContentTemplate>(`/content/${enc(id)}/save-as-template`, body),

  /**
   * テンプレートの適用。**参照ではなくコピー**（CD D41）。
   * 適用すると新しい版が生まれるので、編集中の内容は先に保存しておくこと。
   */
  applyTemplate: (id: string, templateId: string): Promise<{ revisionId: string; revision: number }> =>
    api.post<{ revisionId: string; revision: number }>(`/content/${enc(id)}/apply-template`, {
      templateId,
    }),

  listShareLinks: (id: string): Promise<CountedList<ContentShareLink>> =>
    api.get<CountedList<ContentShareLink>>(`/content/${enc(id)}/share-links`),

  /** 作成。**`token` と `url` は応答の 1 回きり**（DB には sha256 しか残らない）。 */
  createShareLink: (
    id: string,
    body: { revision: number; expiresInDays: number; maxViews?: number; label?: string },
  ): Promise<ContentShareLinkCreated> =>
    api.post<ContentShareLinkCreated>(`/content/${enc(id)}/share-links`, body),

  revokeShareLink: (linkId: string): Promise<{ revoked: boolean }> =>
    api.del<{ revoked: boolean }>(`/content/share-links/${enc(linkId)}`),

  snippetDependents: (snippetId: string): Promise<SnippetDependents> =>
    api.get<SnippetDependents>(`/content/snippets/${enc(snippetId)}/dependents`),

  /* -------------------------------------------------------- 予約実行 */

  listSchedules: (filter: { state?: string; documentId?: string; limit?: number }): Promise<
    CursorPage<ContentSchedule>
  > =>
    api.get<CursorPage<ContentSchedule>>(
      `/content/schedules${toQuery({
        state: filter.state,
        documentId: filter.documentId,
        limit: filter.limit,
      })}`,
    ),

  /** 1 文書につき保留中の予約は 1 本だけ（重複は 409）。 */
  createSchedule: (body: {
    documentId: string;
    revision?: number;
    action: 'approve' | 'archive' | 'unarchive';
    runAt: string;
  }): Promise<ContentSchedule> => api.post<ContentSchedule>('/content/schedules', body),

  /** 取り消し。**行は消さず `cancelled` にする**（誰がいつ予約して取り消したかは残す）。 */
  cancelSchedule: (scheduleId: string): Promise<{ id: string; state: string }> =>
    api.del<{ id: string; state: string }>(`/content/schedules/${enc(scheduleId)}`),

  /* ------------------------------------------------- 取り込み・書き出し */

  listImports: (): Promise<CountedList<ContentImport>> =>
    api.get<CountedList<ContentImport>>('/content/imports'),

  /** ① 受け口を作る。バンドルの実体は presigned PUT でブラウザから直接置く。 */
  createImport: (body: {
    sourceKind?: 'json_bundle' | 'html_file' | 'aurora_template';
    byteSize: number;
  }): Promise<ContentImportCreated> => api.post<ContentImportCreated>('/content/imports', body),

  /** ② 検証。**適用の前に必ず通す**（途中で落ちたときに何が入ったか分からなくなる）。 */
  validateImport: (importId: string): Promise<ContentImportValidation> =>
    api.post<ContentImportValidation>(`/content/imports/${enc(importId)}/validate`),

  /** ③ 適用。1 件ずつ独立したトランザクションなので、部分成功があり得る。 */
  applyImport: (importId: string): Promise<ContentImportApplied> =>
    api.post<ContentImportApplied>(`/content/imports/${enc(importId)}/apply`),

  createExport: (body: {
    documentIds?: string[];
    tags?: string[];
    from?: string;
    to?: string;
    format?: 'json' | 'html';
  }): Promise<ContentExportAccepted> => api.post<ContentExportAccepted>('/content/exports', body),

  exportStatus: (exportId: string): Promise<ContentExportStatus> =>
    api.get<ContentExportStatus>(`/content/exports/${enc(exportId)}`),

  /* ----------------------------------------------------- リンクの確認 */

  runLinkChecks: (id: string, revision: number): Promise<ContentLinkCheckRun> =>
    api.post<ContentLinkCheckRun>(`/content/${enc(id)}/revisions/${revision}/link-checks`),

  linkChecks: (id: string, revision: number): Promise<ContentLinkCheckPage> =>
    api.get<ContentLinkCheckPage>(`/content/${enc(id)}/revisions/${revision}/link-checks`),

  /* ------------------------------------------------------------ 設定 */

  getPolicy: (): Promise<OrgContentPolicy> => api.get<OrgContentPolicy>('/content-policies'),

  updatePolicy: (body: Partial<OrgContentPolicy>): Promise<OrgContentPolicy> =>
    api.put<OrgContentPolicy>('/content-policies', body),

  getLinkPolicy: (): Promise<ContentLinkPolicy> => api.get<ContentLinkPolicy>('/content/link-policy'),

  updateLinkPolicy: (body: Partial<ContentLinkPolicy>): Promise<ContentLinkPolicy> =>
    api.put<ContentLinkPolicy>('/content/link-policy', body),
};

/* ================================================================ メディア */

/**
 * 一覧の絞り込み。
 * **API が受け付けるのは `q` と `folderId` だけ**（`GET /content/assets` の
 * zod スキーマがこの 2 つしか持たない）。未使用の抽出は別エンドポイント
 * （`GET /content/assets/unused?days=`）なので、ここに `unusedDays` を混ぜない。
 */
export interface AssetListFilter {
  q?: string;
  folderId?: string;
  cursor?: string | null;
  limit?: number;
}

export const mediaApi = {
  listAssets: (filter: AssetListFilter): Promise<CursorPage<ContentAsset>> =>
    api.get<CursorPage<ContentAsset>>(
      `/content/assets${toQuery({
        q: filter.q,
        folderId: filter.folderId,
        cursor: filter.cursor,
        limit: filter.limit,
      })}`,
    ),

  /** 1 件取得。**バリアント（派生画像）が付くのはこちらだけ**（一覧には付かない）。 */
  getAsset: (assetId: string): Promise<ContentAsset> =>
    api.get<ContentAsset>(`/content/assets/${enc(assetId)}`),

  /**
   * 未使用の抽出（[47]）。判定式は `sweep.unused-assets` と同じものを API が使う。
   * 画面と GC がずれないよう、**画面側で日数の意味を再定義しない**。
   */
  unusedAssets: (days: number): Promise<UnusedAssetPage> =>
    api.get<UnusedAssetPage>(`/content/assets/unused${toQuery({ days })}`),

  /** 代替テキスト・表示名・タグ・フォルダの後からの変更（[47]）。 */
  updateAsset: (
    assetId: string,
    body: {
      altText?: string;
      title?: string | null;
      tags?: string[];
      folderId?: string | null;
      externalRef?: string | null;
    },
  ): Promise<ContentAsset> => api.patch<ContentAsset>(`/content/assets/${enc(assetId)}`, body),

  /**
   * 実体の差し替え（[47]）。**`mutable=false` なら 409**。
   * 応答の `warning`（送信済みメールの画像も変わる）は必ず画面に出すこと。
   * この後 `commitUpload()` を叩くところまでが 1 操作。
   */
  replaceAsset: (
    assetId: string,
    body: { contentType: string; byteSize: number; sha256: string },
  ): Promise<ContentAssetReplaceTicket> =>
    api.post<ContentAssetReplaceTicket>(`/content/assets/${enc(assetId)}/replace`, body),

  /** 派生画像の作り直し。jobId が assetId 単位なので連打しても二重に積まれない。 */
  deriveAsset: (assetId: string): Promise<{ assetId: string; jobId: string }> =>
    api.post<{ assetId: string; jobId: string }>(`/content/assets/${enc(assetId)}/derive`),

  listFolders: (): Promise<CountedList<ContentAssetFolder>> =>
    api.get<CountedList<ContentAssetFolder>>('/content/folders'),

  createFolder: (body: { name: string; parentId?: string | null }): Promise<ContentAssetFolder> =>
    api.post<ContentAssetFolder>('/content/folders', body),

  updateFolder: (
    folderId: string,
    body: { name?: string; parentId?: string | null },
  ): Promise<ContentAssetFolder> =>
    api.patch<ContentAssetFolder>(`/content/folders/${enc(folderId)}`, body),

  /** **空のときだけ消せる**（中身ごと消せる口は API にも無い）。 */
  deleteFolder: (folderId: string): Promise<{ id: string; deleted: boolean }> =>
    api.del<{ id: string; deleted: boolean }>(`/content/folders/${enc(folderId)}`),

  /** ① presigned PUT の発行。実体は API を通らずブラウザから S3 へ直接送る。 */
  createUpload: (body: {
    fileName: string;
    contentType: string;
    byteSize: number;
    sha256: string;
    folderId?: string | null;
    altText?: string;
    tags?: string[];
    mutable?: boolean;
  }): Promise<ContentAssetUploadTicket> =>
    api.post<ContentAssetUploadTicket>('/content/assets', body, {
      idempotencyKey: newIdempotencyKey(),
    }),

  /** ③ 実体の sha256 とバイト数を API 側で検証させる。 */
  commitUpload: (assetId: string): Promise<ContentAsset> =>
    api.post<ContentAsset>(`/content/assets/${enc(assetId)}/commit`),

  deleteAsset: (assetId: string): Promise<void> => api.del<void>(`/content/assets/${enc(assetId)}`),

  usages: (assetId: string): Promise<CountedList<ContentAssetUsage>> =>
    api.get<CountedList<ContentAssetUsage>>(`/content/assets/${enc(assetId)}/usages`),
};

/** presigned URL への直接 PUT。**業務 API ではない**ので `api.ts` を通さない。 */
export async function uploadToPresignedUrl(
  ticket: ContentAssetUploadTicket,
  file: File,
): Promise<void> {
  const res = await fetch(ticket.upload.url, {
    method: ticket.upload.method,
    headers: ticket.upload.headers,
    body: file,
    // 署名付き URL に Cookie を送ると署名対象外のヘッダが増えて 403 になる環境がある。
    credentials: 'omit',
  });
  if (!res.ok) throw new Error(`ファイルのアップロードに失敗しました（${res.status}）`);
}

/** ブラウザ側で sha256 を計算する。API 側の検証と突き合わせるため必須。 */
export async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/* ============================================================ キャンペーン */

export interface CampaignListFilter {
  q?: string;
  status?: string;
  listId?: string;
  cursor?: string | null;
  limit?: number;
}

/**
 * キャンペーンの状態語彙を DB 側から shared 側へ直す。
 *
 * **DB は `cancelled`（l 2 つ）、`@mail-sv/shared` の状態機械は `canceled`（l 1 つ）**で
 * 1 語だけ綴りがずれている（`ALTER TYPE RENAME VALUE` はしない方針）。API は
 * DB の値をそのまま返すので、SPA 側は**ここ 1 か所で**必ず変換する。
 *
 * 変換を忘れると `CAMPAIGN_TRANSITIONS['cancelled']` が `undefined` になり、
 * `campaignTransition()` が `undefined.validate` を読んで画面が真っ白になる。
 * 実際にそうなっていた（取りやめ済みのキャンペーンが 1 件でもあると一覧が落ちる）。
 */
function fromDbStatus<T extends { status: string }>(row: T): T {
  return { ...row, status: campaignStatusFromDb(row.status) };
}

/**
 * 逆向き。**一覧の絞り込みは DB の語彙で投げる**（API の zod が `DB_CAMPAIGN_STATUS`）。
 * `canceled` のまま送ると 400 になる。`'all'`・未指定はそのまま通す
 * （`toQuery()` が落とす）。
 */
function toDbStatus(status: string | undefined): string | undefined {
  if (!status || status === 'all') return status;
  if (!(CAMPAIGN_STATUS as readonly string[]).includes(status)) return status;
  return campaignStatusToDb(status as CampaignStatus);
}

export const campaignApi = {
  /**
   * 一覧のタイル用の集計（[50]）。**一覧を数え直さない。**
   * `sentThisMonth` は速報値（出典は `campaigns.stats`）で、請求の根拠ではない。
   */
  counts: (): Promise<CampaignCounts> => api.get<CampaignCounts>('/campaigns/summary'),

  list: async (filter: CampaignListFilter): Promise<CursorPage<CampaignSummary>> => {
    const page = await api.get<CursorPage<CampaignSummary>>(
      `/campaigns${toQuery({
        q: filter.q,
        status: toDbStatus(filter.status),
        listId: filter.listId,
        cursor: filter.cursor,
        limit: filter.limit,
      })}`,
    );
    return { ...page, items: page.items.map(fromDbStatus) };
  },

  get: async (id: string): Promise<CampaignDetail> =>
    fromDbStatus(await api.get<CampaignDetail>(`/campaigns/${enc(id)}`)),

  /**
   * 作成。**`contentDocumentId` は必須**（サーバーの zod が `min(1)`）。
   * 「あとで本文を選ぶ」形にすると 400 になるので、作成ダイアログでも必ず選ばせる。
   */
  create: async (body: {
    name: string;
    listId: string;
    contentDocumentId: string;
    sendingStreamId?: string;
  }): Promise<CampaignSummary> =>
    fromDbStatus(
      await api.post<CampaignSummary>('/campaigns', body, { idempotencyKey: newIdempotencyKey() }),
    ),

  update: async (
    id: string,
    body: { name?: string; listId?: string; contentDocumentId?: string; sendingStreamId?: string },
  ): Promise<CampaignDetail> =>
    fromDbStatus(await api.patch<CampaignDetail>(`/campaigns/${enc(id)}`, body)),

  validate: (id: string): Promise<CampaignValidation> =>
    api.post<CampaignValidation>(`/campaigns/${enc(id)}/validate`),

  /** 集計値のみ。受信者単位の一覧は返らない。 */
  report: (id: string): Promise<CampaignReport> =>
    api.get<CampaignReport>(`/campaigns/${enc(id)}/report`),

  /**
   * クリックの内訳（リンク別・時系列）。`/report` と分けてあるのは
   * `campaign_events` を舐める分だけ重いため。レポートのタブでだけ取る。
   */
  clicks: (id: string): Promise<CampaignClickReport> =>
    api.get<CampaignClickReport>(`/campaigns/${enc(id)}/report/clicks`),

  /**
   * CSV 書き出し。**断面ごとに 1 ファイル**（列が揃わない CSV は BI で読めない）。
   * `downloadFile` は認証ヘッダ付きで取得して Blob で保存する
   * （SPA から素の `<a href>` で叩くと 401 になる）。
   */
  downloadReportCsv: (id: string, kind: CampaignReportCsvKind): Promise<void> =>
    downloadFile(`/campaigns/${enc(id)}/report/csv?kind=${kind}`, `campaign-${id}-${kind}.csv`),

  /**
   * 状態を動かす操作はどれも**キャンペーンの全体像を返さない**
   *（`{campaignId, status, ...}` だけ）。成功後は必ず再取得すること。
   */
  schedule: (id: string, scheduledAt: string): Promise<CampaignStateChange> =>
    api.post<CampaignStateChange>(`/campaigns/${enc(id)}/schedule`, { scheduledAt }, {
      idempotencyKey: newIdempotencyKey(),
    }),

  /**
   * 解禁。**人間のセッションからしか押せない**（`campaignSend` は
   * `TOKEN_FORBIDDEN_PERMISSIONS`）。API トークンでの自動化はここで必ず止まる。
   */
  release: (id: string): Promise<CampaignStateChange> =>
    api.post<CampaignStateChange>(`/campaigns/${enc(id)}/release`),

  /** 止める操作は広く開ける（`campaignHalt` はトークンにも許される）。 */
  pause: (id: string, reason?: string): Promise<CampaignStateChange> =>
    api.post<CampaignStateChange>(`/campaigns/${enc(id)}/pause`, reason ? { reason } : {}),

  cancel: (id: string, reason?: string): Promise<CampaignStateChange> =>
    api.post<CampaignStateChange>(`/campaigns/${enc(id)}/cancel`, reason ? { reason } : {}),

  /**
   * 緊急停止（[49]）。**`pause` より強い。**
   * `paused` は「再開できる一時停止」、`halted` は「そのキャンペーンは終わり」で、
   * 遷移表が `halted` から許すのは `cancel` だけ。再開はできない。
   */
  halt: (id: string, reason?: string): Promise<CampaignStateChange> =>
    api.post<CampaignStateChange>(`/campaigns/${enc(id)}/halt`, reason ? { reason } : {}),

  /**
   * 再開（[49]）。**解禁と同じ重さ**（`campaignSend` = 人のセッションのみ）。
   * 止めている間に前提が崩れていることがあるので、API 側でプリフライトを
   * もう一度通す。落ちたら 422 で `blocks` が返る。
   */
  resume: (id: string): Promise<CampaignStateChange> =>
    api.post<CampaignStateChange>(`/campaigns/${enc(id)}/resume`),

  /**
   * テスト送信（[49]）。**宛先は自組織メンバーのアドレスだけ**（最大 5 件）。
   * 任意の宛先に送れると、宛先リストとオプトイン証跡を丸ごと迂回できてしまう。
   */
  testSend: (id: string, to: string[]): Promise<CampaignTestSendResult> =>
    api.post<CampaignTestSendResult>(`/campaigns/${enc(id)}/test-send`, { to }),
};

/* ================================================================== 宛先 */

export const listApi = {
  /**
   * 宛先リストの一覧。
   *
   * **アーカイブ済みは既定で返らない**（サーバー側の既定が
   * `includeArchived=false`）。取り消しの導線を出すには明示的に取りに行く
   * 必要がある。ここを呼ばない限り `POST /lists/:id/unarchive` は
   * 画面から到達できないままになる（実際そうなっていた）。
   */
  list: (includeArchived = false): Promise<CountedList<ContactList>> =>
    api.get<CountedList<ContactList>>(`/lists${includeArchived ? '?includeArchived=true' : ''}`),

  /** アーカイブの取り消し。**名簿はそのまま残っている**ので状態を戻すだけ。 */
  unarchive: (id: string): Promise<ContactList> =>
    api.post<ContactList>(`/lists/${enc(id)}/unarchive`),

  get: (id: string): Promise<ContactList> => api.get<ContactList>(`/lists/${enc(id)}`),

  create: (body: { name: string; description?: string }): Promise<ContactList> =>
    api.post<ContactList>('/lists', body),

  update: (
    id: string,
    body: {
      name?: string;
      description?: string;
      legalFooter?: Record<string, string>;
      /** クリック計測。**開封（`trackOpens`）はサーバーが受け付けない**（実装が無い）。 */
      trackClicks?: boolean;
      /** 公開アーカイブ。有効化した 1 回だけ URL が振られる。 */
      archiveEnabled?: boolean;
    },
  ): Promise<ContactList> => api.patch<ContactList>(`/lists/${enc(id)}`, body),

  contacts: (
    id: string,
    filter: { q?: string; status?: string; cursor?: string | null; limit?: number },
  ): Promise<CursorPage<Contact>> =>
    api.get<CursorPage<Contact>>(
      `/lists/${enc(id)}/contacts${toQuery({
        q: filter.q,
        status: filter.status,
        cursor: filter.cursor,
        limit: filter.limit,
      })}`,
    ),

  /**
   * 宛先の追加。**`optin` は省略できない。**
   * 証跡なしで登録できる経路を残すと、配信前の同意確認が素通りになる。
   */
  addContact: (
    id: string,
    body: {
      email: string;
      displayName?: string;
      attributes?: Record<string, string>;
      optin: { method: string; occurredAt: string; evidence: Record<string, unknown> };
    },
  ): Promise<Contact> => api.post<Contact>(`/lists/${enc(id)}/contacts`, body),

  removeContact: (id: string, contactId: string): Promise<void> =>
    api.del<void>(`/lists/${enc(id)}/contacts/${enc(contactId)}`),

  /* ------------------------------------------------ CSV の一括取り込み */

  imports: (id: string): Promise<CursorPage<ContactImport>> =>
    api.get<CursorPage<ContactImport>>(`/lists/${enc(id)}/imports?limit=50`),

  /**
   * 起票（201）。**同意の取得方法は省略できない**（サーバーも DB も必須）。
   * 応答の `upload.url` へ CSV 本体を PUT する（本体は S3。CP の DB には入らない）。
   */
  createImport: (
    id: string,
    body: { optin: { method: string; occurredAt: string; evidence: Record<string, unknown> } },
  ): Promise<ContactImportCreated2> =>
    api.post<ContactImportCreated2>(`/lists/${enc(id)}/imports`, body),

  /** 検査（202）。**適用の前に必ず通す**（サーバーも `preview_ready` 以外を拒否する）。 */
  validateImport: (id: string, importId: string): Promise<{ id: string; status: string }> =>
    api.post<{ id: string; status: string }>(`/lists/${enc(id)}/imports/${enc(importId)}/validate`),

  /** 適用（202）。**ここから先は取り消せない。** */
  applyImport: (id: string, importId: string): Promise<{ id: string; status: string }> =>
    api.post<{ id: string; status: string }>(`/lists/${enc(id)}/imports/${enc(importId)}/apply`),
};

export const suppressionApi = {
  list: (filter: {
    q?: string;
    reason?: string;
    cursor?: string | null;
    limit?: number;
  }): Promise<CursorPage<Suppression>> =>
    api.get<CursorPage<Suppression>>(
      `/suppressions${toQuery({
        q: filter.q,
        reason: filter.reason,
        cursor: filter.cursor,
        limit: filter.limit,
      })}`,
    ),

  /** 範囲は常に組織全体（キャンペーン単位の停止は受信者側の操作でしか作られない）。 */
  create: (body: { email: string; reason: string; note?: string }): Promise<Suppression> =>
    api.post<Suppression>('/suppressions', body),

  /** `reason='unsubscribe'` は API 側が 403 で拒否する。画面もボタンを出さない。 */
  remove: (id: string): Promise<void> => api.del<void>(`/suppressions/${enc(id)}`),
};

/* ================================================================ Webhook */

export const webhookApi = {
  list: (): Promise<CountedList<WebhookEndpoint>> =>
    api.get<CountedList<WebhookEndpoint>>('/webhooks'),

  create: (body: {
    name: string;
    url: string;
    eventTypes: string[];
    description?: string;
    piiAck?: boolean;
  }): Promise<WebhookEndpointCreated> => api.post<WebhookEndpointCreated>('/webhooks', body),

  update: (
    id: string,
    body: { url?: string; eventTypes?: string[]; description?: string; status?: string },
  ): Promise<WebhookEndpoint> => api.patch<WebhookEndpoint>(`/webhooks/${enc(id)}`, body),

  remove: (id: string): Promise<void> => api.del<void>(`/webhooks/${enc(id)}`),

  /** 旧鍵は `overlapHours` の間だけ併用される（その間は両方の署名が付く）。 */
  rotateSecret: (id: string, overlapHours: number): Promise<WebhookSecretRotated> =>
    api.post<WebhookSecretRotated>(`/webhooks/${enc(id)}/rotate-secret`, { overlapHours }),

  /** 連続失敗で自動停止した購読の復帰。自動復帰はしない（人が直してから押す）。 */
  enable: (id: string): Promise<WebhookEndpoint> =>
    api.post<WebhookEndpoint>(`/webhooks/${enc(id)}/enable`),

  /** 結果が同期で返る。ここが非同期だと「登録したが届かない」の切り分けができない。 */
  test: (id: string): Promise<WebhookTestResult> =>
    api.post<WebhookTestResult>(`/webhooks/${enc(id)}/test`),

  deliveries: (
    id: string,
    filter: { state?: string; eventType?: string; cursor?: string | null; limit?: number },
  ): Promise<CursorPage<WebhookDelivery>> =>
    api.get<CursorPage<WebhookDelivery>>(
      `/webhooks/${enc(id)}/deliveries${toQuery({
        state: filter.state,
        eventType: filter.eventType,
        cursor: filter.cursor,
        limit: filter.limit,
      })}`,
    ),

  redeliver: (id: string, deliveryId: string): Promise<WebhookDelivery> =>
    api.post<WebhookDelivery>(`/webhooks/${enc(id)}/deliveries/${enc(deliveryId)}/redeliver`),

  /**
   * 未配送の残り件数。**救済の前後で「効いたか」を見るための数字**で、
   * これが無いと契約者は replay を何回押せばよいのか分からない。
   */
  replayPending: (): Promise<WebhookReplayPending> =>
    api.get<WebhookReplayPending>('/webhooks/replay/pending'),

  /**
   * 一括の積み直し（`202`）。
   *
   * **積んだだけであって、届いたわけではない。** 応答の `note` も同じことを
   * 言っている。画面でもここを曖昧にせず、結果は配送ログで確認させる。
   */
  replay: (body: { since?: string; limit?: number; endpointId?: string }): Promise<WebhookReplayResult> =>
    api.post<WebhookReplayResult>('/webhooks/replay', body),
};
